import { AlertTriangle, ShieldCheck } from "lucide-react";
import { DataTable, LoadingBlock, Pill, Section, StatCard, useAdminResource } from "./AdminUI";
import { formatDateTime } from "../../lib/datetime";

export default function SecurityPanel() {
  const { data, loading, error } = useAdminResource("/admin/security");
  if (loading) return <LoadingBlock label="Loading security posture" />;
  if (error) return <div className="brutal-card p-6 text-ink-accent text-sm" role="alert">{error}</div>;

  const problems = data.config_problems || [];
  const ok = problems.length === 0;

  return (
    <>
      <Section id="posture" title="// DEPLOYMENT POSTURE">
        <div
          className={`brutal-card p-6 mb-4 ${ok ? "bg-state-success-surface" : "bg-state-danger-surface"}`}
          role={ok ? "status" : "alert"}
        >
          <div className="flex items-start gap-3">
            {ok ? <ShieldCheck size={20} className="text-state-success shrink-0 mt-0.5" aria-hidden="true" />
                : <AlertTriangle size={20} className="text-ink-accent shrink-0 mt-0.5" aria-hidden="true" />}
            <div>
              <div className="font-display font-bold type-subheading">
                {ok ? "No configuration problems" : `${problems.length} configuration problem${problems.length === 1 ? "" : "s"}`}
              </div>
              <p className="text-sm text-ink-muted mt-1">
                These are the same checks that refuse to boot the backend in production
                (<code className="font-mono-ui">config_check.collect_problems</code>).
                {!data.is_production && " This deploy is not marked production, so they are not currently enforced."}
              </p>
              {!ok && (
                <ul className="mt-3 space-y-2">
                  {problems.map((p) => (
                    <li key={p} className="text-sm flex gap-2">
                      <span aria-hidden="true">·</span><span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <StatCard label="Environment" value={data.environment}
                    hint={data.is_production ? "Production checks enforced" : "Development mode"}
                    tone={data.is_production ? "good" : "warn"} />
          <StatCard label="Suspended accounts" value={data.accounts.suspended}
                    tone={data.accounts.suspended > 0 ? "warn" : "default"} />
          <StatCard label="Unverified accounts" value={data.accounts.unverified}
                    hint="Never confirmed their email"
                    tone={data.accounts.unverified > 0 ? "warn" : "default"} />
          <StatCard label="Active sessions" value={data.sessions.active} />
          <StatCard label="High-risk actions (7d)" value={data.high_risk_actions_7d}
                    hint="Plan changes, suspensions, deletions" />
          <StatCard label="Mock billing" value={data.mock_billing ? "ON" : "off"}
                    hint={data.mock_billing ? "Anyone can self-upgrade for free" : "Stripe key configured"}
                    tone={data.mock_billing ? "bad" : "good"} />
        </div>
      </Section>

      {(data.duplicate_accounts || []).length > 0 && (
        <Section id="dupes" title="// DUPLICATE ACCOUNTS">
          <div className="brutal-card p-6 bg-state-warning-surface" role="alert">
            <p className="text-sm mb-4">
              These mailboxes have more than one account, differing only by letter case.
              The user's plan, projects and quota sit on one of them while they may be
              signing in to the other. Merging means choosing which plan and which data
              survive, so it is left to you rather than done automatically.
            </p>
            {data.duplicate_accounts.map((d) => (
              <div key={d.email} className="mb-4 last:mb-0">
                <div className="font-mono-ui text-xs font-semibold mb-1">{d.email} — {d.count} accounts</div>
                <ul className="space-y-1">
                  {d.accounts.map((a) => (
                    <li key={a.user_id} className="font-mono-ui text-xs flex flex-wrap gap-3 text-ink-muted">
                      <span className="text-ink">{a.email}</span>
                      <span>plan={a.plan || "free"}</span>
                      <span>auth={a.auth_provider}</span>
                      <span>created={formatDateTime(a.created_at)}</span>
                      <span>{a.user_id}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section id="admins" title="// ADMIN ACCESS">
        <div className="brutal-card p-6">
          {data.admin_dev_mode && (
            <p className="text-sm text-ink-accent mb-3" role="alert">
              <code className="font-mono-ui">ADMIN_DEV_MODE</code> is on — every authenticated user is an admin.
            </p>
          )}
          <div className="text-2xs uppercase tracking-widest text-ink-muted mb-2">
            Authorised by ADMIN_EMAILS ({data.admin_emails.length})
          </div>
          <ul className="flex flex-wrap gap-2">
            {data.admin_emails.map((e) => <li key={e}><Pill tone="accent">{e}</Pill></li>)}
            {data.admin_emails.length === 0 && <li className="text-ink-subtle text-sm">None configured.</li>}
          </ul>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 mt-6 text-sm">
            {[["Total accounts", data.accounts.total], ["Password auth", data.accounts.password_auth],
              ["OAuth accounts", data.accounts.oauth]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3 border-b border-rule-faint py-1">
                <span className="text-ink-muted">{k}</span><span className="tabular-nums">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section id="highrisk" title="// RECENT HIGH-RISK ACTIONS">
        <DataTable
          caption="Recent privileged actions that changed access, billing, or data"
          empty="No high-risk actions recorded."
          columns={[{ key: "at", label: "When" }, { key: "actor", label: "Admin" },
                    { key: "action", label: "Action" }, { key: "target", label: "Target" }]}
          rows={data.recent_high_risk}
          renderRow={(a) => (
            <tr key={a.audit_id} className="border-t border-rule">
              <td className="p-3 font-mono-ui text-xs whitespace-nowrap">{formatDateTime(a.at)}</td>
              <td className="p-3 font-mono-ui text-xs break-all">{a.actor}</td>
              <td className="p-3"><Pill tone="danger">{a.action}</Pill></td>
              <td className="p-3 font-mono-ui text-xs text-ink-muted break-all">
                {a.detail?.email || a.target_id || "—"}
              </td>
            </tr>
          )}
        />
      </Section>
    </>
  );
}
